import { useState, useCallback, useEffect, useMemo } from "react";
import {
    computeEqualSplitsNumeric,
    isCustomValidNumeric,
} from "features/groups/lib/transactionSplits";
import { Username, SplitDetails, GroupTransaction } from "@/types/GroupTransaction";

// -----------------------------------------------------------------------------
//  Locals
// -----------------------------------------------------------------------------

export type SplitType = "equal" | "custom";

// -----------------------------------------------------------------------------
//  Hooks
// -----------------------------------------------------------------------------

export interface UseAddGroupTransactionResult {
    people: Username[];                                         // initial roster (read-only here)
    newTransaction: GroupTransaction;
    validationError: string;
    setField: <K extends keyof GroupTransaction>(
        key: K,
        value: GroupTransaction[K]
    ) => void;
    handleSplitChange: (personId: Username, amount: number) => void;
    handleSplitTypeChange: (udpatedValue: SplitType) => void;
    onTogglePerson: (personId: Username) => void;
    reset: () => void;
}

// When splitType is "equal", default to including all people
function defaultEqualSplitDetails(people: Username[]): SplitDetails {
    return people.length > 0 ? Object.fromEntries(people.map((p) => [p, 0])) : {};
}

// ---------------------------------------------------------------------------------------
export function useAddGroupTransaction(initialPeople: Username[] = []): UseAddGroupTransactionResult {
    const people = useMemo(
        () => Array.from(new Set(initialPeople.map((personId) => String(personId).trim()).filter(Boolean))),
        [initialPeople],
    );
    
    const [validationError, setValidationError] = useState<string>("");
    const [newTransaction, setNewTransaction] = useState<GroupTransaction>(() => ({
        name: "",
        amount: 0,
        payer: "",
        splitType: "equal",
        splitDetails: defaultEqualSplitDetails(people),
        currency: "ARS", // Default currency
        date: "0",
        _id: "",
        category: undefined,
    }));

    useEffect(() => {
        setNewTransaction((previousTransaction) => {
            const currentIds = Object.keys(previousTransaction.splitDetails ?? {});
            const samePeople =
                currentIds.length === people.length &&
                currentIds.every((id) => people.includes(id));

            if (samePeople) return previousTransaction;

            const nextBaseDetails = people.reduce<SplitDetails>((acc, personId) => {
                acc[personId] = previousTransaction.splitDetails?.[personId] ?? 0;
                return acc;
            }, {});

            return {
                ...previousTransaction,
                splitDetails: previousTransaction.splitType === "equal"
                    ? computeEqualSplitsNumeric(previousTransaction.amount, nextBaseDetails)
                    : nextBaseDetails,
            };
        });
    }, [people]);
   
    // ---------------------------------------------------------------------------------
    const setField = useCallback<
        <K extends keyof GroupTransaction>(
            key: K,
            value: GroupTransaction[K]
        ) => void
    >((key, value) => {
        setNewTransaction((previousTransaction) => ({
            ...previousTransaction,
            [key]: value,
        }));
    }, []);
    
    useEffect(() => {
        setNewTransaction((previousTransaction) => {
            if (previousTransaction.splitType !== "equal") return previousTransaction;
            return {
                ...previousTransaction,
                splitDetails: computeEqualSplitsNumeric(
                    previousTransaction.amount,
                    previousTransaction.splitDetails
                )
            };
        });
    }, [newTransaction.amount])

    useEffect(() => {
        if (newTransaction.splitType !== "custom") {
            setValidationError("");
            return;
        }
        const isValid = isCustomValidNumeric(
            newTransaction.amount,
            newTransaction.splitDetails
        );
        setValidationError(isValid ? "" : "Total splits must equal transaction amount");
    }, [newTransaction.splitType, newTransaction.amount, newTransaction.splitDetails]);

    //! updates values of splits when split type changes
    const handleSplitTypeChange = useCallback(( updatedType :  SplitType ) => {
        
        // update the transaction
        setField("splitType", updatedType);
        
        if (updatedType === "equal") {
            // Default to everyone when switching to equal split
            const allPeopleDetails = defaultEqualSplitDetails(people);
            let updatedSplitDetails: SplitDetails = computeEqualSplitsNumeric(newTransaction.amount, allPeopleDetails);
            
            setNewTransaction((previousTransaction) => ({
                ...previousTransaction,
                splitDetails: updatedSplitDetails
            }));
            
            setValidationError("");
        }
    }, [newTransaction.amount, setField, people]);

    //! modifies value of the amount due for each person while validating the state
    const handleSplitChange = useCallback((personId: Username, amount: number) => {
        if (newTransaction.splitType === "custom") {
            const updatedSplitDetails: SplitDetails = {
                ...newTransaction.splitDetails,
                [personId]: amount,
            };
            
            setNewTransaction((previousTransaction) => ({
                ...previousTransaction,
                splitDetails: updatedSplitDetails
            }));

        }
        else {
            console.log("useAddGroupTransaction::handleSplitChange - Unknown splitType");
        }
    }, [newTransaction.splitType, newTransaction.splitDetails]);

    //! adds and removes people from the transaction split
    const onTogglePerson = useCallback((personId: Username) => {
        
        setNewTransaction((previousTransaction) => {
            // Check if the person is currently included
            const isIncluded: boolean = Object.prototype.hasOwnProperty.call(
                previousTransaction.splitDetails,
                personId
            );

            if (isIncluded) {
                const { [personId]: _removed, ...remainingSplits } = previousTransaction.splitDetails;
                
                let updatedSplitDetails: SplitDetails = computeEqualSplitsNumeric(previousTransaction.amount, remainingSplits);

                return {
                    ...previousTransaction,
                    splitDetails: updatedSplitDetails
                };

            } else {
                const temSplitDetails: SplitDetails = {
                    ...previousTransaction.splitDetails,
                    [personId]: previousTransaction.amount
                };            
                
                let updatedSplitDetails: SplitDetails = computeEqualSplitsNumeric(previousTransaction.amount, temSplitDetails);

                return {
                    ...previousTransaction,
                    splitDetails: updatedSplitDetails,
                };
            }
        });
    }, []);

    // ---------------------------------------------------------------------------------
    const reset = useCallback(() => {
        setNewTransaction({
            name: "",
            amount: 0,
            payer: "",
            splitType: "equal",
            splitDetails: defaultEqualSplitDetails(people),
            currency: "ARS", // Default currency
            _id: "",
            date: "0",
            category: undefined,
            liquidityPoolId: undefined,
        });
        setValidationError("");
    }, [people]);

    return {
        people,
        newTransaction,
        validationError,
        setField,
        handleSplitChange,
        handleSplitTypeChange,
        onTogglePerson,
        reset,
    };
}
